"use client";

import {
  Button,
  Flex,
  IconButton,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react";
import IconBadge from "@components/common/IconBadge";
import { ColumnHeader } from "@components/common/TableHelpers";
import {
  cargoItemFootprint,
  footprintFormatters,
  totalCargoFootprint,
} from "helpers/pricingUnit";
import type { ContainerPricingUnit } from "helpers/pricingUnit";
import type { CargoItem } from "@features/services/shipping/types";
import { useMemo, useState } from "react";
import { LuPackage, LuPlus, LuTrash2 } from "react-icons/lu";
import ExpandableSearchButton from "@components/common/ExpandableSearchButton";

interface CargoItemsTableProps {
  items: CargoItem[];
  pricingUnit: ContainerPricingUnit;
  onRemove: (id: string) => void;
  onAddMore: () => void;
  isProcessing?: boolean;
  locked?: boolean;
}

const CargoItemsTable = ({
  items,
  pricingUnit,
  onRemove,
  onAddMore,
  isProcessing,
  locked,
}: CargoItemsTableProps) => {
  const fmt = useMemo(() => footprintFormatters(pricingUnit), [pricingUnit]);
  const totalUnits = useMemo(
    () => totalCargoFootprint(items, pricingUnit),
    [items, pricingUnit],
  );
  const [searchQuery, setSearchQuery] = useState("");

  const query = searchQuery.trim().toLowerCase();
  const filteredItems =
    query === ""
      ? items
      : items.filter((item) => item.description.toLowerCase().includes(query));

  return (
    <Flex direction="column" gap={2}>
      <Flex justify="flex-end" gap={2}>
        <ExpandableSearchButton query={searchQuery} setQuery={setSearchQuery} />
        {locked !== true && (
          <Button
            variant="ghost"
            size="xs"
            onClick={onAddMore}
            disabled={isProcessing}
          >
            {isProcessing ? <Spinner size="xs" /> : <LuPlus />}
          </Button>
        )}
      </Flex>
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row bg="surface.muted">
            <ColumnHeader>Item description</ColumnHeader>
            <ColumnHeader textAlign="end">{fmt.columnLabel}</ColumnHeader>
            {locked !== true && <ColumnHeader w="40px" />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.length > 0 && filteredItems.length === 0 && (
            <Table.Row>
              <Table.Cell border="none" p={3} colSpan={locked !== true ? 3 : 2}>
                <Text fontSize="sm" color="text.secondary">
                  No items matching &ldquo;{searchQuery.trim()}&rdquo;.
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
          {filteredItems.map((item) => (
            <Table.Row key={item.id} fontSize="sm">
              <Table.Cell border="none" p={3}>
                <Flex alignItems="center" gap={2}>
                  <IconBadge>
                    <LuPackage size={14} />
                  </IconBadge>
                  <Text fontSize="sm" fontWeight="medium">
                    {item.description}
                  </Text>
                </Flex>
              </Table.Cell>
              <Table.Cell border="none" p={3} textAlign="end">
                <Text fontSize="sm">
                  {fmt.formatItemDisplay(cargoItemFootprint(item, pricingUnit))}
                </Text>
              </Table.Cell>
              {locked !== true && (
                <Table.Cell border="none" p={1}>
                  <IconButton
                    aria-label="Remove item"
                    variant="ghost"
                    size="xs"
                    onClick={() => onRemove(item.id)}
                  >
                    <LuTrash2 />
                  </IconButton>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
          <Table.Row fontSize="sm" fontWeight="bold">
            <Table.Cell borderTop="1px solid" borderColor="border.light" p={3}>
              <Text fontSize="sm" fontWeight="semibold">
                Total
              </Text>
            </Table.Cell>
            <Table.Cell
              borderTop="1px solid"
              borderColor="border.light"
              p={3}
              textAlign="end"
            >
              <Text fontSize="sm" fontWeight="semibold">
                {fmt.formatTotal(totalUnits)}
              </Text>
            </Table.Cell>
            {locked !== true && (
              <Table.Cell
                borderTop="1px solid"
                borderColor="border.light"
                w="40px"
              />
            )}
          </Table.Row>
        </Table.Body>
      </Table.Root>
    </Flex>
  );
};

export default CargoItemsTable;
